/**
 * The guide part chip — the one affordance that says which half of a guide
 * something belongs to. There is no glyph: the part hue (`--accent-you` deep
 * teal, `--accent-together` plum) and the word ("You" / "Together") carry the
 * part.
 *
 * The `/library` CARD is its one surface. Every pill renders in its part hue
 * and appends at most one lowercase `qualifier` that DESCRIBES the half —
 * `writing` / `waiting` / `hidden` — set in the same case, size and tracking
 * as the part word with lightness the only difference, so the pill reads as
 * one label (CSS uppercases it and adds the parentheses). Anything actionable
 * lives in the card's right rail, not on a pill.
 *
 * The merged reader's masthead used to carry a chip pair too, one per half.
 * It no longer does: the masthead states the whole document's facts in the
 * app's two-pill meta row, and which half is which — with its own date — is
 * the contents map's job.
 *
 * @module components/guidePartMark
 */

import { escapeHtml } from '../core/utils.js';

/** The two parts a guide can have. */
export const GUIDE_PART_YOU = 'you';
export const GUIDE_PART_TOGETHER = 'together';

const PART_WORD = { [GUIDE_PART_YOU]: 'You', [GUIDE_PART_TOGETHER]: 'Together' };

/**
 * One part chip: the part word plus an optional qualifier.
 *
 * @param {Object} opts
 * @param {'you'|'together'} opts.part - Which part the chip names
 * @param {string} [opts.qualifier=''] - Lowercase descriptive qualifier
 *   rendered as `.guide-part-chip__meta--qualifier` ("writing", "waiting",
 *   "hidden"); escaped here
 * @returns {string} HTML string
 */
export function renderPartChip({ part, qualifier = '' } = {}) {
  const key = part === GUIDE_PART_TOGETHER ? 'together' : 'you';
  const meta = qualifier
    ? `<span class="guide-part-chip__meta guide-part-chip__meta--qualifier">${escapeHtml(qualifier)}</span>`
    : '';
  return `<span class="guide-part-chip guide-part-chip--${key}"><span class="guide-part-chip__word">${PART_WORD[key]}</span>${meta}</span>`;
}
